// ============================================================================
// WAREHOUSE MANAGEMENT SYSTEM - COMPLETE REWRITE
// Solid logic based on Supabase rental_records schema
// jsQR library loaded for real QR code decoding from camera
// ============================================================================

// Load jsQR library (lightweight QR decoder) - with multiple CDN options
let jsQRLoaded = false;

function loadJsQR() {
  if (window.jsQR) {
    console.log('✅ jsQR already loaded');
    jsQRLoaded = true;
    return;
  }

  // Try primary CDN first
  const script1 = document.createElement('script');
  script1.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
  script1.async = true;
  script1.onload = () => {
    console.log('✅ jsQR loaded from jsDelivr CDN');
    jsQRLoaded = true;
  };
  script1.onerror = () => {
    console.warn('⚠️ jsQR failed from jsDelivr, trying unpkg...');
    // Fallback to unpkg CDN
    const script2 = document.createElement('script');
    script2.src = 'https://unpkg.com/jsqr@1.4.0/dist/jsQR.js';
    script2.async = true;
    script2.onload = () => {
      console.log('✅ jsQR loaded from unpkg CDN');
      jsQRLoaded = true;
    };
    script2.onerror = () => {
      console.error('❌ Failed to load jsQR from both CDNs - manual input fallback will be used');
    };
    document.head.appendChild(script2);
  };
  document.head.appendChild(script1);
}

// Load jsQR on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadJsQR);
} else {
  loadJsQR();
}

(function() {
  'use strict';

  class WarehouseApp {
    constructor() {
      this.API_URL = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
      this.PASSWORD = 'warehouse2024';
      this.authenticated = false;
      this.currentView = 'rentals'; // 'rentals' or 'returns'
      this.allOrders = [];
      this.allReturns = [];
      this.autoRefreshTimer = null;
      this.tagsCache = {}; // variant_id -> tags array
      this.orderScanState = {}; // orderId -> { bookIdx: { scanned: bool, verified: bool, tag: string } }
      this.scannerActive = null; // Current order/book being scanned

      this.init();
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================
    init() {
      this.checkAuth();
      this.attachListeners();
      
      if (this.authenticated) {
        this.showInterface(); // use single consistent name
        this.loadData();
        this.startAutoRefresh();
      } else {
        this.showPasswordScreen();
      }
    }
    
    checkAuth() {
      // Standardize stored value to 'true'
      this.authenticated = sessionStorage.getItem('warehouseAuth') === 'true';
    }
    
    attachListeners() {
      // Password form
      const form = document.getElementById('password-form');
      if (form) form.addEventListener('submit', (e) => this.handlePassword(e));
      
      // Tab switching
      const rentalTab = document.getElementById('tab-rentals');
      const returnTab = document.getElementById('tab-returns');
      if (rentalTab) rentalTab.addEventListener('click', () => this.switchView('rentals'));
      if (returnTab) returnTab.addEventListener('click', () => this.switchView('returns'));
      
      // Force refresh button
      const refreshBtn = document.getElementById('force-refresh');
      if (refreshBtn) refreshBtn.addEventListener('click', () => this.loadData());
    }
    
    // Ensure handler sets exact 'true' string used by checkAuth()
    handlePassword(e) {
      e.preventDefault();
      const input = document.querySelector('#password-form input');
      if (!input) return;
      if (input.value === this.PASSWORD) {
        sessionStorage.setItem('warehouseAuth', 'true'); // standardized
        this.authenticated = true;
        this.showInterface();
        this.loadData();
        this.startAutoRefresh();
      } else {
        this.showError('Incorrect password.'); 
      }
      input.value = '';
    }

    submitPassword() {
      const input = document.getElementById('warehouse-password');
      const pwd = input ? input.value : '';
      const errorEl = document.getElementById('password-error');
      
      if (pwd === this.PASSWORD) {
        // Store a consistent flag value
        sessionStorage.setItem('warehouseAuth', 'true');
        this.authenticated = true;
        if (input) input.value = '';
        if (errorEl) errorEl.style.display = 'none';
        this.showInterface();
        this.loadData();
        this.startAutoRefresh();
      } else {
        this.showError('Incorrect password');
        if (input) input.value = '';
        if (errorEl) errorEl.style.display = 'block';
      }
    }

    showPasswordScreen() {
      const pwd = document.getElementById('password-screen');
      const main = document.getElementById('warehouse-interface');
      if (pwd) pwd.style.display = 'flex';
      if (main) main.style.display = 'none';
    }

    // showInterface displays the main warehouse UI
    showInterface() {
      const pwd = document.getElementById('password-screen');
      const main = document.getElementById('warehouse-interface');
      if (pwd) pwd.style.display = 'none';
      if (main) main.style.display = 'block';
      
      // Initialize view to rentals (shows the rental-orders-section)
      const rentalSec = document.getElementById('rental-orders-section');
      const returnSec = document.getElementById('return-requests-section');
      if (rentalSec) rentalSec.style.display = 'block';
      if (returnSec) returnSec.style.display = 'none';
      
      // Update tab styling
      const rentalTab = document.getElementById('tab-rentals');
      const returnTab = document.getElementById('tab-returns');
      if (rentalTab) rentalTab.classList.add('toggle-btn-active');
      if (returnTab) returnTab.classList.remove('toggle-btn-active');
    }

    renderOrder(order) {
      const books = order.books || [];
      // Initialize scan state for this order if not exists
      if (!this.orderScanState[order.id]) {
        this.orderScanState[order.id] = {};
        books.forEach((_, idx) => {
          this.orderScanState[order.id][idx] = { scanned: false, verified: false, tag: '' };
        });
      }

      const booksHtml = books
        .map((book, idx) => {
          const variantId = book.variant_id || '';
          // product_id may be a Shopify gid (e.g. "gid://shopify/Product/7831301128310")
          const productGid = book.product_id || '';
          const productIdMatch = String(productGid).match(/(\d+)$/);
          const productId = productIdMatch ? productIdMatch[1] : '';
          const tagId = `tag-${order.id}-${variantId || productId}-${idx}`;
          let tag = 'Loading...';

          // Use cached tags if available (try variant first, then product)
          if (variantId && this.tagsCache[variantId] && Array.isArray(this.tagsCache[variantId])) {
            tag = this.tagsCache[variantId].length ? this.tagsCache[variantId].join(', ') : 'No tags';
          } else if (productId && this.tagsCache[productId] && Array.isArray(this.tagsCache[productId])) {
            tag = this.tagsCache[productId].length ? this.tagsCache[productId].join(', ') : 'No tags';
          }

          // Trigger fetch to get real Shopify tags (pass both ids)
          this.fetchAndDisplayTags(variantId, productId, tagId);

          // Get scan state for this book
          const scanState = this.orderScanState[order.id][idx];
          let statusIcon = '📷'; // default scan button
          let statusClass = 'pending-scan';
          
          if (scanState.verified) {
            statusIcon = '✅';
            statusClass = 'scan-verified';
          } else if (scanState.scanned && !scanState.verified) {
            statusIcon = '❌';
            statusClass = 'scan-failed';
          }

          return `
            <div class="book-row" data-variant-id="${this.escape(variantId)}" data-product-id="${this.escape(productId)}" data-order-id="${this.escape(order.id)}" data-book-idx="${idx}">
              <img src="${this.escape(book.image_url || '')}" 
                   alt="book" 
                   class="book-img"
                   onerror="this.src='https://via.placeholder.com/50x70/ddd/999?text=📚'">
              <div class="book-details">
                <strong>${this.escape(book.title || 'Untitled')}</strong>
                <small>Tag: <span class="tag" id="${tagId}">${this.escape(tag)}</span></small>
              </div>
              <button class="btn-scan ${statusClass}" onclick="warehouseApp.startScanning('${this.escape(order.id)}', ${idx})">
                ${statusIcon} ${scanState.verified ? 'Verified' : 'Scan'}
              </button>
            </div>
          `;
        })
        .join('');

      // Check if all books are verified to show Complete Order button
      const allVerified = Object.values(this.orderScanState[order.id] || {}).every(state => state.verified);
      const completeButtonDisplay = allVerified ? 'block' : 'none';

      return `
        <div class="order-box">
          <div class="order-header-row">
            <span class="order-num">Order #${this.escape(order.order_number || order.id.substring(0, 8))}</span>
            <span class="order-date">${this.formatDate(order.created_at)}</span>
            <span class="badge status-${order.status}">${order.status}</span>
          </div>
          <div class="books-list">
            ${booksHtml || '<p style="color: #999; font-size: 12px;">No books in this order</p>'}
          </div>
          <div class="order-actions">
            <button class="btn btn-primary" style="display: ${completeButtonDisplay}" onclick="warehouseApp.completeOrder('${this.escape(order.id)}')">
              ✓ Complete Order
            </button>
            <p style="display: ${allVerified ? 'none' : 'block'}; color: #999; font-size: 12px; margin-top: 8px;">
              Scan all books to unlock Complete Order button
            </p>
          </div>
        </div>
      `;
    }

    // Fetch tags from backend and update UI
    // Tries multiple endpoints (variant, product) and caches results.
    async fetchAndDisplayTags(variantId, productId, tagElementId) {
      // pick a cache key (prefer variant, fallback to product)
      const key = variantId || productId;
      if (!key) return;

      // If we already have a resolved array cached, update UI immediately
      if (this.tagsCache[key] && Array.isArray(this.tagsCache[key])) {
        const tagEl = document.getElementById(tagElementId);
        if (tagEl) tagEl.textContent = this.tagsCache[key].length ? this.tagsCache[key].join(', ') : 'No tags';
        return;
      }

      // If an in-flight fetch promise exists, await it and update UI
      if (this.tagsCache[key] && typeof this.tagsCache[key].then === 'function') {
        try {
          const tags = await this.tagsCache[key];
          const tagEl = document.getElementById(tagElementId);
          if (tagEl) tagEl.textContent = tags.length ? tags.join(', ') : 'No tags';
        } catch (e) {
          const tagEl = document.getElementById(tagElementId);
          if (tagEl) tagEl.textContent = 'No tags';
        }
        return;
      }

      // Build candidate endpoints to try (most specific first)
      const candidates = [];
      if (variantId) {
        candidates.push(`${this.API_URL}/shopify/product-tags/${variantId}`);
        candidates.push(`${this.API_URL}/shopify/product-tags/variant/${variantId}`);
      }
      if (productId) {
        candidates.push(`${this.API_URL}/shopify/product-tags/product/${productId}`);
        candidates.push(`${this.API_URL}/shopify/product-tags/${productId}`);
      }

      // Start a fetch promise and store it to avoid duplicates
      const fetchPromise = (async () => {
        for (const url of candidates) {
          try {
            const res = await fetch(url);
            if (!res.ok) continue;
            const data = await res.json();
            const tags = Array.isArray(data.tags) ? data.tags : [];
            return tags;
          } catch (err) {
            // try next
            continue;
          }
        }
        return [];
      })();

      this.tagsCache[key] = fetchPromise;

      try {
        const tags = await fetchPromise;
        // cache the resolved tags array under both variant and product keys if available
        if (variantId) this.tagsCache[variantId] = tags;
        if (productId) this.tagsCache[productId] = tags;

        const tagEl = document.getElementById(tagElementId);
        if (tagEl) tagEl.textContent = tags.length ? tags.join(', ') : 'No tags';
      } catch (err) {
        if (variantId) this.tagsCache[variantId] = [];
        if (productId) this.tagsCache[productId] = [];
        const tagEl = document.getElementById(tagElementId);
        if (tagEl) tagEl.textContent = 'No tags';
      }
    }

    // (removed duplicate showMain; showInterface above now handles UI display)

    // ========================================================================
    // DATA LOADING
    // ========================================================================
    async loadData() {
      try {
        this.showLoading();
        
        const [ordersRes, returnsRes] = await Promise.all([
          fetch(`${this.API_URL}/warehouse/rental-orders`),
          fetch(`${this.API_URL}/warehouse/return-requests`)
        ]);

        if (!ordersRes.ok || !returnsRes.ok) throw new Error('API error');

        const ordersData = await ordersRes.json();
        const returnsData = await returnsRes.json();
        
        console.log('DEBUG: Raw API response (orders):', ordersData);
        console.log('DEBUG: Raw API response (returns):', returnsData);

        this.allOrders = (ordersData.orders || []).filter(o => o.status !== 'shipped');
        this.allReturns = returnsData.returns || [];
        
        console.log(`DEBUG: Loaded ${this.allOrders.length} orders and ${this.allReturns.length} returns`);
        console.log('DEBUG: First order sample:', this.allOrders[0]);

        // Update stats
        const rentalsCountEl = document.getElementById('pending-rentals-count');
        const returnsCountEl = document.getElementById('pending-returns-count');
        if (rentalsCountEl) rentalsCountEl.textContent = this.allOrders.length;
        if (returnsCountEl) returnsCountEl.textContent = this.allReturns.length;

        this.render();
        this.hideLoading();
      } catch (err) {
        console.error('Load error:', err);
        this.showError('Failed to load data');
      }
    }

    // ========================================================================
    // RENDERING
    // ========================================================================
    render() {
      if (this.currentView === 'rentals') {
        this.renderRentals();
      } else {
        this.renderReturns();
      }
    }

    renderRentals() {
      const container = document.getElementById('rental-orders-list');
      if (!container) return;

      console.log('DEBUG: All orders:', this.allOrders);
      
      if (this.allOrders.length === 0) {
        container.innerHTML = '<div class="empty-msg">No pending rental orders</div>';
        return;
      }

      // GROUP BY CUSTOMER_ID
      const groups = {};
      this.allOrders.forEach(order => {
        const customerId = order.customer_id || 'unknown';
        if (!groups[customerId]) {
          groups[customerId] = {
            customer_id: customerId,
            customer_name: order.customer_name || 'Unknown Customer',
            customer_email: order.customer_email || 'No Email',
            orders: []
          };
        }
        groups[customerId].orders.push(order);
      });

      console.log('DEBUG: Grouped orders:', groups);

      // RENDER EACH CUSTOMER GROUP
      container.innerHTML = Object.values(groups)
        .map(group => this.renderCustomerGroup(group))
        .join('');
    }

    renderCustomerGroup(group) {
      const totalBooks = group.orders.reduce((sum, o) => sum + (o.total_books || 0), 0);
      
      return `
        <div class="customer-section">
          <div class="customer-header">
            <div>
              <h3>${this.escape(group.customer_name)}</h3>
              <p>${this.escape(group.customer_email)}</p>
            </div>
            <div class="customer-meta">
              <span class="badge">${group.orders.length} order(s)</span>
              <span class="badge">${totalBooks} books</span>
            </div>
          </div>
          
          <div class="orders-grid">
            ${group.orders.map(order => this.renderOrder(order)).join('')}
          </div>
        </div>
      `;
    }

    renderReturns() {
      const container = document.getElementById('return-requests-list');
      if (!container) return;

      if (this.allReturns.length === 0) {
        container.innerHTML = '<div class="empty-msg">No return requests</div>';
        return;
      }

      container.innerHTML = this.allReturns
        .map(ret => `
          <div class="return-box">
            <div class="return-header-row">
              <strong>${this.escape(ret.customer_name || 'Customer')}</strong>
              <span class="badge status-${ret.status}">${ret.status}</span>
            </div>
            <p>${this.escape(ret.customer_email || '')}</p>
            <p>Books: ${ret.books_count || 0}</p>
            <button class="btn btn-primary" onclick="warehouseApp.completeReturn('${this.escape(ret.id)}')">
              ✓ Mark Collected
            </button>
          </div>
        `)
        .join('');
    }

    // ========================================================================
    // ACTIONS
    // ========================================================================
    async completeOrder(orderId) {
      try {
        console.log(`✅ Completing order: ${orderId}`);
        
        // Show loading state - find and disable the button
        const btn = document.querySelector(`button[onclick*="completeOrder('${this.escape(orderId)}')"]`);
        if (btn) {
          btn.disabled = true;
          btn.textContent = '⏳ Processing...';
        }

        // Send request to backend - use checkout_id (orderId in this context)
        const response = await fetch(`${this.API_URL}/warehouse/update-order-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: orderId,
            status: 'shipped'
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to complete order');
        }

        const result = await response.json();
        console.log(`✅ Order completed successfully:`, result);
        
        // Show success toast
        this.showSuccess('Order processing completed!');
        
        // Wait a moment then reload data
        setTimeout(() => {
          console.log('🔄 Reloading warehouse data...');
          this.loadData();
        }, 500);

      } catch (error) {
        console.error('❌ Error completing order:', error);
        this.showError(`Could not complete order: ${error.message}`);
        
        // Re-enable button on error
        const btn = document.querySelector(`button[onclick*="completeOrder('${this.escape(orderId)}')"]`);
        if (btn) {
          btn.disabled = false;
          btn.textContent = '✓ Complete Order';
        }
      }
    }

    async completeReturn(returnId) {
      try {
        const res = await fetch(`${this.API_URL}/warehouse/update-return-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ return_id: returnId, status: 'collected' })
        });

        if (!res.ok) throw new Error('Failed');
        
        this.showSuccess('Return collected!');
        await this.loadData();
      } catch (err) {
        this.showError('Could not update return');
      }
    }

    scanBook(orderId, bookIndex) {
      alert(`Scanning book ${bookIndex + 1} from order ${orderId.substring(0, 8)}...\n\nQR code scanning feature coming soon!`);
    }

    // ========================================================================
    // QR CODE SCANNING
    // ========================================================================
    async startScanning(orderId, bookIndex) {
      const order = this.allOrders.find(o => o.id === orderId);
      if (!order || !order.books || !order.books[bookIndex]) {
        this.showError('Book not found');
        return;
      }

      const book = order.books[bookIndex];
      const expectedTag = this.getBookTag(book);

      // Create scanner modal
      const modal = document.createElement('div');
      modal.id = 'scanner-modal';
      modal.className = 'scanner-modal';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.95);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      `;

      modal.innerHTML = `
        <div style="color: white; text-align: center; width: 90%; max-width: 500px;">
          <h2 style="margin-bottom: 16px;">📷 Scan QR Code</h2>
          <p style="margin-bottom: 8px; font-size: 14px;">Book: <strong>${this.escape(book.title || 'Unknown')}</strong></p>
          <p style="margin-bottom: 20px; font-size: 14px; color: #ccc;">Expected Tag: <strong>${this.escape(expectedTag)}</strong></p>
          <video id="qr-scanner" style="width: 100%; max-width: 400px; border-radius: 8px; margin-bottom: 20px; background: #000;"></video>
          <div id="scanner-result" style="min-height: 40px; margin-bottom: 20px; font-size: 14px; color: #aaa;">
            <small>Scanning... point camera at QR code</small>
          </div>
          <div style="display: flex; gap: 10px; justify-content: center;">
            <button onclick="warehouseApp.cancelScanning()" style="padding: 10px 20px; background: #999; color: white; border: none; border-radius: 4px; cursor: pointer;">Cancel</button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      // Start camera stream
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' } 
        });
        const video = document.getElementById('qr-scanner');
        video.srcObject = stream;
        video.play();

        // Store stream and order info for scanning
        this.currentScanStream = stream;
        this.currentScanOrder = { orderId, bookIndex, expectedTag };

        // Start QR code detection
        this.detectQRCode(video, modal);
      } catch (err) {
        console.error('❌ Camera access error:', err);
        this.showError('Unable to access camera. Please check permissions.');
        modal.remove();
      }
    }

    detectQRCode(video, modal) {
      const resultDiv = document.getElementById('scanner-result');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      let lastDetectedQR = null;
      let detectionStable = 0;
      const self = this;
      let jsqrReady = false;
      let manualInputAdded = false;

      const detectFrame = () => {
        if (!self.currentScanStream) {
          console.log('📹 Scanner stream stopped');
          modal.remove();
          return;
        }

        try {
          // Check if jsQR library is loaded
          if (typeof window.jsQR !== 'function') {
            if (!jsqrReady) {
              console.log('⏳ jsQR not available yet, checking...');
              console.log('window.jsQR:', typeof window.jsQR);
              jsqrReady = true;
            }

            // Add manual fallback input if jsQR isn't ready
            if (!manualInputAdded) {
              console.log('📝 Adding manual input fallback since jsQR not loaded');
              const fallbackDiv = document.createElement('div');
              fallbackDiv.style.cssText = 'margin-top: 15px;';
              fallbackDiv.innerHTML = `
                <input type="text" id="manual-qr-input" placeholder="Tap to enter tag manually" 
                  style="width: 90%; padding: 10px; border: 1px solid #555; border-radius: 4px; 
                         color: white; background: rgba(255,255,255,0.1); text-align: center; font-size: 14px;">
              `;
              resultDiv.parentElement.insertBefore(fallbackDiv, resultDiv.nextSibling);
              
              const input = document.getElementById('manual-qr-input');
              input.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                  console.log('📝 Manual tag entry:', input.value);
                  self.verifyScannedTag(input.value, modal, resultDiv);
                  input.value = '';
                }
              });
              manualInputAdded = true;
            }
            
            requestAnimationFrame(detectFrame);
            return;
          }

          // jsQR is ready, remove manual input if it exists
          if (manualInputAdded) {
            const manualInput = document.getElementById('manual-qr-input');
            if (manualInput && manualInput.parentElement) {
              manualInput.parentElement.remove();
            }
            manualInputAdded = false;
            console.log('✅ jsQR loaded! Removed manual input fallback');
          }

          // Set canvas size to match video
          if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
          }

          // Draw current video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Get image data and pass to jsQR for detection
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const qrCode = window.jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert'
          });

          if (qrCode) {
            const scannedData = qrCode.data;
            console.log('🔍 QR detected:', scannedData);

            // Debounce: only process if same QR detected multiple frames in a row
            if (scannedData === lastDetectedQR) {
              detectionStable++;
              console.log(`📊 Stable frames: ${detectionStable}/3`);
              if (detectionStable >= 3) {
                console.log('✅ Stable QR detection confirmed after 3 frames:', scannedData);
                self.verifyScannedTag(scannedData, modal, resultDiv);
                lastDetectedQR = null;
                detectionStable = 0;
              }
            } else {
              lastDetectedQR = scannedData;
              detectionStable = 1;
              console.log('🆕 New QR code detected, starting stable detection...');
            }
          } else {
            if (lastDetectedQR) {
              lastDetectedQR = null;
              detectionStable = 0;
            }
          }
        } catch (err) {
          console.error('❌ QR detection error:', err);
        }

        requestAnimationFrame(detectFrame);
      };

      console.log('📷 Starting QR detection loop...');
      console.log('window.jsQR available:', typeof window.jsQR);
      detectFrame();
    }

    verifyScannedTag(scannedData, modal, resultDiv) {
      const { orderId, bookIndex, expectedTag } = this.currentScanOrder;
      const scannedTag = String(scannedData).trim().toUpperCase();
      const expectedTagUpper = String(expectedTag).trim().toUpperCase();

      if (scannedTag === expectedTagUpper) {
        // Correct tag
        this.orderScanState[orderId][bookIndex].scanned = true;
        this.orderScanState[orderId][bookIndex].verified = true;
        resultDiv.innerHTML = `<span style="color: #00ff00; font-weight: bold;">✅ Correct! Tag verified: ${this.escape(scannedTag)}</span>`;

        // Close scanner after 1 second
        setTimeout(() => {
          this.cancelScanning();
          this.render(); // Re-render to update button states
        }, 1000);
      } else {
        // Wrong tag
        this.orderScanState[orderId][bookIndex].scanned = true;
        this.orderScanState[orderId][bookIndex].verified = false;
        resultDiv.innerHTML = `<span style="color: #ff4444; font-weight: bold;">❌ Wrong tag!</span><br><small style="color: #ccc;">Expected: ${this.escape(expectedTagUpper)}<br/>Scanned: ${this.escape(scannedTag)}</small>`;

        // Allow retry: reset input and keep scanner open
        const input = document.getElementById('qr-input-fallback');
        if (input) input.value = '';
      }
    }

    cancelScanning() {
      if (this.currentScanStream) {
        this.currentScanStream.getTracks().forEach(track => track.stop());
        this.currentScanStream = null;
      }
      const modal = document.getElementById('scanner-modal');
      if (modal) modal.remove();
      this.currentScanOrder = null;
    }

    getBookTag(book) {
      // Prefer cached tag from Shopify; fallback to tag in book object
      if (book.variant_id && this.tagsCache[book.variant_id]) {
        const cachedTags = this.tagsCache[book.variant_id];
        if (Array.isArray(cachedTags) && cachedTags.length > 0) {
          return cachedTags[0]; // Return first tag (e.g., W89)
        }
      }
      
      if (book.product_id) {
        const productIdMatch = String(book.product_id).match(/(\d+)$/);
        const productId = productIdMatch ? productIdMatch[1] : '';
        if (productId && this.tagsCache[productId]) {
          const cachedTags = this.tagsCache[productId];
          if (Array.isArray(cachedTags) && cachedTags.length > 0) {
            return cachedTags[0];
          }
        }
      }

      // Fallback to tags array in book if present
      if (book.tags && Array.isArray(book.tags) && book.tags.length > 0) {
        return book.tags[0];
      }

      return 'Unknown';
    }

    // ========================================================================
    // VIEW SWITCHING
    // ========================================================================
    switchView(view) {
      this.currentView = view;

      // Update tabs
      const rentalTab = document.getElementById('tab-rentals');
      const returnTab = document.getElementById('tab-returns');
      if (rentalTab) {
        rentalTab.classList.toggle('toggle-btn-active', view === 'rentals');
      }
      if (returnTab) {
        returnTab.classList.toggle('toggle-btn-active', view === 'returns');
      }

      // Update sections
      const rentalSec = document.getElementById('rental-orders-section');
      const returnSec = document.getElementById('return-requests-section');
      if (rentalSec) rentalSec.style.display = view === 'rentals' ? 'block' : 'none';
      if (returnSec) returnSec.style.display = view === 'returns' ? 'block' : 'none';

      this.render();
    }

    // ========================================================================
    // AUTO REFRESH
    // ========================================================================
    startAutoRefresh() {
      this.autoRefreshTimer = setInterval(() => {
        if (this.authenticated) {
          console.log('🔄 Auto-refreshing warehouse data...');
          this.loadData();
        }
      }, 60000); // 60 seconds - refreshes every minute
    }

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    }

    // ========================================================================
    // UI HELPERS
    // ========================================================================
    showLoading() {
      const container = document.getElementById('rental-orders-list');
      if (container) container.innerHTML = '<div class="loading">Loading...</div>';
    }

    hideLoading() {
      // Handled by render
    }

    showError(msg) {
      const toast = document.createElement('div');
      toast.className = 'toast error';
      toast.textContent = '❌ ' + msg;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 4000);
    }

    showSuccess(msg) {
      const toast = document.createElement('div');
      toast.className = 'toast success';
      toast.textContent = '✅ ' + msg;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }

    escape(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  // ============================================================================
  // INITIALIZE APP
  // ============================================================================
  window.warehouseApp = null;

  document.addEventListener('DOMContentLoaded', () => {
    window.warehouseApp = new WarehouseApp();
  });

  window.addEventListener('beforeunload', () => {
    if (window.warehouseApp) {
      window.warehouseApp.stopAutoRefresh();
    }
  });
})();
